import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import CmsGallery from '../components/CmsGallery';
import { getCmsPage } from '../lib/cms';

export default function AboutPage() {
  const [page, setPage] = useState(null);

  useEffect(() => {
    let isMounted = true;
    getCmsPage('/om-kromix').then((data) => {
      if (isMounted && data) setPage(data);
    });
    return () => {
      isMounted = false;
    };
  }, []);
  return (
    <>
      <SeoHead
        title={page?.meta_title || "Om KROMIX A/S | Historien & Råvarerne"}
        description={page?.meta_description || "Historien om Kromix siden 1986. Henning Kromann, jagthunde og kompromisløse råvarer i høj kvalitet til hunde og katte."}
      />

      {/* Hero Header */}
      <section className="bg-[#39463A] text-white py-14 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-3">
            <span className="text-xs font-bold uppercase tracking-widest text-[#DED3C0] block">
              {page?.badge_tekst || 'Dansk foder siden 1986'}
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
              {page?.overskrift || 'Historien om KROMIX'}
            </h1>
            <p className="text-base sm:text-lg text-[#FCFAF5]/90 leading-relaxed">
              {page?.undertekst || 'Vi skaffer resultater – rigtig ernæring, sund levevis. Læs om hvordan passionen for jagthunde og kompromisløs kvalitet lagde fundamentet for KROMIX A/S.'}
            </p>
          </div>
        </div>
      </section>

      {/* Main Narrative Section: Strictly Verbatim from Scrape */}
      <section className="py-14 sm:py-20 bg-[#F4EFE5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {/* Chapter 1: Historien om Kromix */}
          <div className="bg-[#FCFAF5] rounded-3xl p-8 sm:p-12 border border-[#DED3C0] shadow-sm space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#39463A] text-white flex items-center justify-center font-bold text-sm">
                1
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#39463A]">
                Historien om Kromix
              </h2>
            </div>

            <div className="space-y-4 text-sm sm:text-base text-[#39463A]/80 leading-relaxed">
              <p>
                Kromix blev stiftet af Henning Kromann i 1986 der igennem hele sit liv havde arbejdet med jagthunde hvor han både har været dommer ved udstillinger og markprøver.
              </p>
              <p>
                I hans arbejde med jagthunde kom han til den realation at der ikke var et fodder på markedet der dækkede hans behov og der blev ideen til Kromix født, et godt kvalitetsfoder til en fair pris.
              </p>
              <p>
                Siden 1986 har Kromix hjulpet danskerne med personlig support med hensyn til ernæring og foder til både hunde og katte, dyrenes behov har altid været i centrum for Kromix derfor bruges der kun råvare af højeste kvalitet.
              </p>
            </div>
          </div>

          {/* Chapter 2: Råvarer i høj kvalitet */}
          <div className="bg-[#FCFAF5] rounded-3xl p-8 sm:p-12 border border-[#DED3C0] shadow-sm space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#71806A] text-white flex items-center justify-center font-bold text-sm">
                2
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#39463A]">
                Råvare i høj kvalitet
              </h2>
            </div>

            <div className="space-y-4 text-sm sm:text-base text-[#39463A]/80 leading-relaxed">
              <p>
                Kromix har råvare i fokus dette betyder hermed at vi kun anvender råvare af højkvalitet så vi dermed sikre os høj optagelse af ernæring, der tilsættes ingen aromaer, smags eller farvestofffer da kvalitet er i prioitet. Alle råvare er testet og bliver løbende testet så vi sikrer at kun den bedste kvalitets råvare bliver anvendt til Kromix.
              </p>
              <p>
                Fodertyperne og sammensætningen har ændret sig gennem årene for at følge med i udviklingen på markedet så vi sikrer os den bedste kvalitets foder til dig og dit kæledyr.
              </p>
              <div className="p-4 rounded-2xl bg-[#F4EFE5] border border-[#DED3C0] flex items-start gap-3 mt-4">
                <i className="fa-solid fa-shield-check text-[#71806A] text-xl mt-1" aria-hidden="true" />
                <p className="text-xs sm:text-sm text-[#39463A]">
                  Samtidig er Kromix tilknyttet fødevarestyrelsen, som foretager stikprøver til analyse, som hjælper med foderhygiejnekontrol for at sikrer den bedste kvalitet af råvare er anvendt.
                </p>
              </div>
            </div>
          </div>

          {/* Chapter 3: Sortiment */}
          <div className="bg-[#FCFAF5] rounded-3xl p-8 sm:p-12 border border-[#DED3C0] shadow-sm space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#71806A] text-white flex items-center justify-center font-bold text-sm">
                3
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#39463A]">
                Sortiment
              </h2>
            </div>
            <p className="text-sm sm:text-base text-[#39463A]/80 leading-relaxed">
              Vores sortiment omfatter mærkerne KROMIX til både hunde og katte, og JOSERA til hunde og katte.
            </p>
            <div className="pt-4 flex flex-wrap gap-4">
              <Link
                to="/hundefoder"
                className="px-5 py-3 rounded-xl bg-[#71806A] hover:bg-[#5C6A56] text-white text-xs font-bold uppercase tracking-wider transition-colors shadow-xs"
              >
                Udforsk hundefoder
              </Link>
              <Link
                to="/josera"
                className="px-5 py-3 rounded-xl bg-[#FCFAF5] hover:bg-[#F4EFE5] border border-[#DED3C0] text-[#39463A] text-xs font-bold uppercase tracking-wider transition-colors"
              >
                Læs om Josera
              </Link>
            </div>
          </div>

          {/* Dynamic Gallery: Messestand, Udstillinger & Biler */}
          <div className="bg-[#FCFAF5] rounded-3xl p-8 sm:p-12 border border-[#DED3C0] shadow-sm">
            <CmsGallery
              referenceSlug="udstilling-og-aktivitet"
              title="KROMIX på udstillinger og de danske landeveje"
              subtitle="Glimt fra vores messestand, Herning udstillingen og vores distributionsbiler."
            />
          </div>
        </div>
      </section>
    </>
  );
}
